import { useState, useEffect, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import QRCode from "qrcode";

// Icons
import { FaMapMarkerAlt, FaPhone, FaFolderPlus, FaPeopleCarry, FaTrash, FaCalendarAlt, FaLock, FaPrint, FaArrowLeft, FaFileInvoiceDollar } from "react-icons/fa";
import { FaPenToSquare, FaUser } from "react-icons/fa6";
import { MdAssignmentAdd, MdTimer } from "react-icons/md";
import { AiFillTags } from "react-icons/ai";
import { IoTime } from "react-icons/io5";
import { RiInfoCardLine, RiMailAddFill } from "react-icons/ri";

// Bootstrap
import { CardBody, Card, Row, Col, Button, Modal, Form, Spinner, Table } from "react-bootstrap";

// Assets
import logoNiralakka from "../../assets/img/logo_Niralakka_1200x1200_blue.svg";

import { reopenTicket } from "../../api/TICKET/ticket";

// Hooks
import { useTicketById } from "../../hooks/TICKET/useGetTicketById";
import { useDeleteIntervention } from "../../hooks/TICKET/useTicketInterventions";
import { useContratsByClientId } from "../../hooks/useGetContratClient";
import { useUsers } from "../../hooks/useGetUsers";
import { useCloseTicket } from "../../hooks/TICKET/useCloseTicket";
import { useDeleteTicket } from "../../hooks/TICKET/useDeleteTicket";
import { useFacturationModes } from "../../hooks/TICKET/useFacturationModes";
import { useClientChamps } from "../../hooks/useClientChamps";
import { getApplicableChamps, TICKET_TO_FIELD_STATUS } from "../../utils/customFields";
import CustomFieldInput from "../../components/CustomFields/CustomFieldInput";
import { postChampValeur } from "../../api/CHAMPS/champs";
import { useChampValeursTicket } from "../../hooks/CHAMPS/useChampValeursTicket";
import {
  useTicketTransports,
  useCreateTransport,
  useDeleteTransport,
} from "../../hooks/TICKET/useTransport";

// Modals
import NewIntervention from "../../components/Modals/Create/NewIntervention";
import EditTicket from "../../components/Modals/Update/EditTicket";
import EditIntervention from "../../components/Modals/Update/EditIntervention";
import TransportModal from "../../components/Modals/Create/TransportModal";
import TicketFiles from "./TicketFiles";
import MarkdownRenderer from "../../components/MarkdownEditor/MarkdownRenderer";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// "Contrat de maintenance" is inactive in DB so the API never returns it.
// Inject it manually when the client has an active contract.
const CONTRAT_MAINTENANCE = { id: 1, label: "Contrat de maintenance" };
const escapeHtml = (unsafe) => {
  if (unsafe == null) return "";
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

/** Map priority label to Bootstrap variant + display label */
const getPriorityBadge = (priority) => {
  if (!priority) return { label: "Low", variant: "secondary" };
  switch (priority.toLowerCase()) {
    case "urgent": return { label: "Urgent", variant: "danger" };
    case "high":   return { label: "High",   variant: "warning" };
    case "medium": return { label: "Medium", variant: "primary" };
    case "low":    return { label: "Low",    variant: "secondary" };
    default:       return { label: priority, variant: "secondary" };
  }
};

/** DD/MM/YYYY à HH:MM */
const formatDateTime = (value) => {
  if (!value) return "Non renseigné";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "Non renseigné";
  return (
    d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " à " +
    d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  );
};

/** 1 h 20 min / 20 min */
const formatDuration = (totalMinutes) => {
  if (!totalMinutes) return "Non renseignée";
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h} h ${m} min`;
  if (h > 0) return `${h} h`;
  return `${m} min`;
};

const COLLAPSED_TEXT_MAX_HEIGHT_REM = 7;


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Collapsible markdown block with "show more / show less" */
const ExpandableText = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const measureOverflow = () => {
      const el = contentRef.current;
      if (!el) {
        setCanExpand(false);
        return;
      }

      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const maxHeightPx = COLLAPSED_TEXT_MAX_HEIGHT_REM * rootFontSize;
      setCanExpand(el.scrollHeight > maxHeightPx + 1);
    };

    measureOverflow();
    window.addEventListener("resize", measureOverflow);
    return () => window.removeEventListener("resize", measureOverflow);
  }, [text]);

  if (!text?.trim()) return <p className="mb-4 text-muted fst-italic">Pas d'information</p>;

  // Truncation is done on the container via CSS max-height to stay markdown-safe
  return (
    <div className="mb-4">
      <div
        ref={contentRef}
        style={!expanded && canExpand ? { maxHeight: `${COLLAPSED_TEXT_MAX_HEIGHT_REM}rem`, overflow: "hidden", position: "relative" } : {}}
      >
        <MarkdownRenderer content={text} />
        {!expanded && canExpand && (
          <div
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              height: "2.5rem",
              background: "linear-gradient(to bottom, transparent, var(--bs-body-bg))",
            }}
          />
        )}
      </div>
      {canExpand && (
        <Button variant="link" size="sm" className="ps-0" style={{ color: "var(--bs-blue)", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#b8c7f2"} onMouseLeave={(e) => e.target.style.color = "var(--bs-blue)"} onClick={() => setExpanded(!expanded)}>
          {expanded ? "Afficher moins" : "Afficher plus"}
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { hasPermission } = useAuth();

  

  

  // Navigation context (back button)
  const fromList  = location.state?.from;
  const listState = location.state?.listState;

  // ── Modal visibility states ───────────────────────────────────────────────
  const [showNewIntervention,   setShowNewIntervention]   = useState(false);
  const [showEditTicket,        setShowEditTicket]        = useState(false);
  const [showEditIntervention,  setShowEditIntervention]  = useState(false);
  const [showConfirmClose,      setShowConfirmClose]      = useState(false);
  const [showConfirmReopen,     setShowConfirmReopen]     = useState(false);
  const [showDeleteModal,       setShowDeleteModal]       = useState(false);
  const [showTransportModal,    setShowTransportModal]    = useState(false);
  const [showDeleteTransport,   setShowDeleteTransport]   = useState(false);
  const [showDeleteTicketModal, setShowDeleteTicketModal] = useState(false);

  // ── Other local states ────────────────────────────────────────────────────
  const [selectedIntervention,    setSelectedIntervention]    = useState(null);
  const [idToDelete,              setIdToDelete]              = useState(null);
  const [selectedFacturationMode, setSelectedFacturationMode] = useState(null);
  const [customFieldValues,       setCustomFieldValues]       = useState({});
  const [deletingTransportId,     setDeletingTransportId]     = useState(null);

  // ── Remote data ───────────────────────────────────────────────────────────
  const { data, isLoading, isError }                                  = useTicketById(ticketId);
  const clientId                                                       = data?.data?.client?.id;
  const { data: contratData }                                          = useContratsByClientId(clientId);
  const { data: facturationModesData, isLoading: facturationLoading } = useFacturationModes();
  const { data: transportsData, isLoading: transportsLoading }        = useTicketTransports(ticketId);
  const { data: clientChampsData }                                     = useClientChamps(clientId);
  const { data: champValeursData }                                      = useChampValeursTicket(ticketId);
  const { data: usersData }                                             = useUsers();

  // ── Mutations ─────────────────────────────────────────────────────────────
  const deleteMutation      = useDeleteIntervention();
  const closeTicketMutation = useCloseTicket();
  const deleteTicketMutation = useDeleteTicket();
  const createTransport     = useCreateTransport(ticketId);
  const deleteTransport     = useDeleteTransport(ticketId);
  const queryClient         = useQueryClient();

  const reopenMutation = useMutation({
  mutationFn: reopenTicket,
  onSuccess: () => {
    toast.success("Ticket réouvert avec succès !");
    setShowConfirmReopen(false);
    queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
    queryClient.invalidateQueries({ queryKey: ["inactiveTickets"] });
  },
  onError: (err) => toast.error("Erreur: " + (err.message || "Réouverture impossible")),
});

  // ── Derived values ────────────────────────────────────────────────────────
  const ticket           = data?.data;
  const client           = ticket?.client;
  const approver         = ticket?.user_approver_id
    ? (usersData?.data ?? []).find(u => u.id === ticket.user_approver_id)
    : null;
  const sortedInterventions = useMemo(() => {
    const interventions = ticket?.ticket_interventions || [];
    return [...interventions].sort((a, b) => {
      const aTime = new Date(a?.start_date || a?.created_at || 0).getTime();
      const bTime = new Date(b?.start_date || b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [ticket]);
  const facturationModes = useMemo(() => facturationModesData?.data || [], [facturationModesData]);
  const contracts        = Array.isArray(contratData?.data) ? contratData.data : [];
  const contract         = contracts[0] ?? null;
  const hasContract      = contracts.length > 0;

  // API response: { data: [{ id, tracking_number, price }] }
  const transports = Array.isArray(transportsData?.data) ? transportsData.data : [];

  // Ticket status flags
  const status         = (typeof ticket?.status === "string" ? ticket.status : ticket?.status?.label)?.toLowerCase();
  const isClientActive = client?.is_active !== false;
  const isEditable     = status !== "closed" && status !== "approved"&& isClientActive;
  const isClosed       = status === "closed";
  const isApproved     = status === "approved";
  const isInProgress   = status === "in_progress";

  // Champs personnalisés applicables à la clôture
  // Inclut les étapes sautées (ex. open → closed affiche aussi les champs 'in_progress')
  const clientChamps       = clientChampsData?.data ?? [];
  const currentFieldStatus = TICKET_TO_FIELD_STATUS[status] ?? 'open';
  const champsACloturer    = getApplicableChamps(clientChamps, currentFieldStatus, 'closed');

  // Priority badge
  const priorityLabel = ticket?.priority?.label || ticket?.priority;
  const priorityBadge = getPriorityBadge(priorityLabel);

  // "Contrat de maintenance" is inactive in DB so the API never returns it.
  // Inject it manually when the client has an active contract.
  const availableFacturationModes = useMemo(() => [
    ...(hasContract ? [CONTRAT_MAINTENANCE] : []),
    ...facturationModes.filter(m => m.id !== 1 && (m.is_active || m.is_default)),
  ], [hasContract, facturationModes]);

  // ── Effects ────────────────────────────────────────────────────────────────────────────

  // Auto-select billing method when the close modal opens.
  useEffect(() => {
    if (!showConfirmClose || availableFacturationModes.length === 0 || selectedFacturationMode) return;
    setSelectedFacturationMode(hasContract ? CONTRAT_MAINTENANCE.id : availableFacturationModes[0]?.id ?? null);
  }, [showConfirmClose, availableFacturationModes, selectedFacturationMode, hasContract]);


  // ── Early returns ─────────────────────────────────────────────────────────
  if (isLoading) return <div className="p-4 text-center">Chargement...</div>;
  if (isError || !ticket) return <div className="p-4 text-center text-danger">Ticket non trouvé</div>;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const openDeleteModal = (id) => {
    setIdToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
  deleteMutation.mutate({ ticketId, interventionId: idToDelete }, {
    onSuccess: () => {
      toast.success("Intervention supprimée !");
      setShowDeleteModal(false);
      setIdToDelete(null);


      queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
    },
    onError: (err) => toast.error("Erreur: " + (err.response?.data?.message || "Suppression impossible")),
  });
};

  const handleReopenTicket = () => reopenMutation.mutate(ticketId);

  const handleCloseTicket = async () => {
    if (!selectedFacturationMode) {
      toast.error("Veuillez sélectionner une méthode de facturation");
      return;
    }
    try {
      await closeTicketMutation.mutateAsync({ id: ticketId, facturationModeId: selectedFacturationMode });

      // Enregistrement des valeurs de champs personnalisés
      if (Object.keys(customFieldValues).length > 0) {
        await Promise.all(
          Object.entries(customFieldValues)
            .filter(([, valeur]) => valeur !== "" && valeur != null)
            .map(([champId, valeur]) => postChampValeur(Number(champId), ticketId, valeur))
        );
      }

      toast.success("Ticket clôturé avec succès !");
      setShowConfirmClose(false);
      setSelectedFacturationMode(null);
      setCustomFieldValues({});
    } catch (err) {
      toast.error("Erreur: " + (err.message || "Impossible de clôturer le ticket"));
    }
  };

  const handleConfirmDeleteTicket = () => {
  deleteTicketMutation.mutate(ticketId, {
    onError: (err) =>
      toast.error("Erreur: " + (err.message || "Suppression impossible")),
  });
};
  // Transport handlers
  const handleTransportSubmit = (formData) => {
    createTransport.mutate(formData, {
      onSuccess: () => {
        toast.success("Transport ajouté !");
        setShowTransportModal(false);
      },
      onError: (err) => toast.error("Erreur: " + err.message),
    });
  };

  const openDeleteTransport = (id) => {
    setDeletingTransportId(id);
    setShowDeleteTransport(true);
  };

  const handleConfirmDeleteTransport = () => {
    deleteTransport.mutate(deletingTransportId, {
      onSuccess: () => {
        toast.success("Transport supprimé !");
        setShowDeleteTransport(false);
        setDeletingTransportId(null);
      },
      onError: (err) => toast.error("Erreur: " + err.message),
    });
  };

  /** Open a print window with ticket details and QR code */
  const handlePrint = async () => {
    try {
      const printWindow = window.open("", "_blank", "width=900,height=1000");
      if (!printWindow) { toast.error("Impossible d'ouvrir la fenêtre d'impression."); return; }

      const intervHtml = sortedInterventions.map(i => `
        <div class="interv">
          <div><strong>Date:</strong> ${escapeHtml(new Date(i.start_date).toLocaleString())}</div>
          <div><strong>Intervenant:</strong> ${escapeHtml(i.user_name || "")}</div>
          <div><strong>Durée:</strong> ${i.duration ? Math.floor(i.duration / 60) + "h" + (i.duration % 60) + "m" : "N/A"}</div>
          <div><strong>Description:</strong><pre>${escapeHtml(i.description || "")}</pre></div>
        </div>
      `).join("");

      // Use tracking_number and price from the actual API response shape
      const transportHtml = transports.length > 0
        ? transports.map(t => `
          <div class="interv">
            <div><strong>N° suivi:</strong> ${escapeHtml(t.tracking_number || "")}</div>
            <div><strong>Prix:</strong> ${t.price != null ? escapeHtml(String(t.price)) + " €" : "—"}</div>
          </div>`).join("")
        : "<div>Aucun transport</div>";

      const priorityColor = { danger: "#dc3545", warning: "#f6c23e", primary: "#4e73df" }[priorityBadge.variant] ?? "#6c757d";
      const ticketUrl = `${window.location.origin}/ticket/${ticket.id}`;

      let qrDataUrl = "";
      try {
        qrDataUrl = await QRCode.toDataURL(ticketUrl, { width: 140, margin: 1 });
      } catch {
        qrDataUrl = `https://chart.googleapis.com/chart?cht=qr&chs=200x200&chl=${encodeURIComponent(ticketUrl)}&chld=L|1`;
      }

      const html = `<!doctype html><html><head><meta charset="utf-8"/>
        <title>Ticket ${escapeHtml(ticket.id)}</title>
        <style>
          body{font-family:Arial,Helvetica,sans-serif;color:#111;padding:20px}
          h1{font-size:18px;margin:0 0 6px}
          .section{margin-bottom:16px}
          pre{white-space:pre-wrap;font-family:inherit;background:#fafafa;padding:8px;border:1px solid #eee}
          .interv{border-top:1px dashed #ddd;padding-top:8px;margin-top:8px}
          .badge{display:inline-block;padding:6px 10px;color:#fff;border-radius:6px}
          .ticket-box{border:1px solid #e6e6e6;padding:12px;border-radius:6px}
          @media print{.no-print{display:none}}
        </style></head><body>
        <div style="display:flex;align-items:flex-start;justify-content:space-between">
          <img src="${logoNiralakka}" alt="Niralakka" style="width:120px;height:auto"/>
          <img src="${qrDataUrl}" alt="QR" style="width:100px;height:100px"/>
        </div>
        <div style="margin-top:12px">
          <h1>Ticket #${escapeHtml(ticket.id)} — ${escapeHtml(ticket.title || "")}</h1>
          <div style="display:flex;gap:12px;margin-top:6px">
            <div><strong>Client:</strong> ${escapeHtml(client?.name || "")}</div>
            <div><strong>Date début:</strong> ${escapeHtml(ticket.start_date?.replace("T", " ").slice(0, 19) || "")}</div>
            <div><strong>Priorité:</strong> <span class="badge" style="background:${priorityColor}">${escapeHtml(priorityBadge?.label || "")}</span></div>
          </div>
        </div>
        <div class="section ticket-box" style="margin-top:12px">
          <div><strong>Description / Consignes:</strong></div>
          <pre>${escapeHtml(ticket.description || "")}</pre>
        </div>
        <div class="section"><h3>Interventions</h3>${intervHtml || "<div>Aucune intervention</div>"}</div>
        <div class="section"><h3>Suivi transport</h3>${transportHtml}</div>
        <div class="no-print" style="margin-top:20px;text-align:right">
          <small>Imprimé le ${new Date().toLocaleString()}</small>
        </div>
        <script>window.onload=function(){window.print();setTimeout(()=>window.close(),500)}</script>
        </body></html>`;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (err) {
      console.error("Print failed", err);
      toast.error("Erreur lors de l'impression");
    }
  };

  // ── Sub-component: single intervention row ────────────────────────────────

  const InterventionItem = ({ intervention }) => {
    const [expanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const contentRef = useRef(null);
    const description = intervention.description || "";

    useEffect(() => {
      const measureOverflow = () => {
        const el = contentRef.current;
        if (!el) {
          setCanExpand(false);
          return;
        }

        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        const maxHeightPx = COLLAPSED_TEXT_MAX_HEIGHT_REM * rootFontSize;
        setCanExpand(el.scrollHeight > maxHeightPx + 1);
      };

      measureOverflow();
      window.addEventListener("resize", measureOverflow);
      return () => window.removeEventListener("resize", measureOverflow);
    }, [description]);

    return (
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex flex-wrap align-items-start gap-3">
            <div className="d-flex align-items-start">
              <FaCalendarAlt className="text-muted mt-1 me-2" size={16} />
              <div>
                <div className="text-muted small">Date</div>
                <div>{formatDateTime(intervention.start_date)}</div>
              </div>
            </div>
            <div className="d-flex align-items-start">
              <FaPeopleCarry className="text-muted mt-1 me-2" size={16} />
              <div>
                <div className="text-muted small">Intervenant</div>
                <div>{intervention.user ? `${intervention.user.name} ${intervention.user.surname}` : "Non assigné"}</div>
              </div>
            </div>
            <div className="d-flex align-items-start">
              <MdTimer className="text-muted mt-1 me-2" size={18} />
              <div>
                <div className="text-muted small">Durée</div>
                <div>{formatDuration(intervention.duration)}</div>
              </div>
            </div>
          </div>
          {isEditable && (
            <div className="d-flex gap-2">
              {hasPermission("EDIT_INTERVENTION") && (
                <Button variant="link" size="sm" className="p-1 text-warning"
                  onClick={() => { setSelectedIntervention(intervention); setShowEditIntervention(true); }}>
                  <FaPenToSquare size={14} />
                </Button>
              )}
              {hasPermission("DELETE_INTERVENTION") && (
                <Button variant="link" size="sm" className="p-1 text-danger"
                  onClick={() => openDeleteModal(intervention.id)}>
                  <FaTrash size={14} />
                </Button>
              )}
            </div>
          )}
        </div>
        <div>
          <div
            ref={contentRef}
            className="mt-3"
            style={!expanded && canExpand ? { maxHeight: `${COLLAPSED_TEXT_MAX_HEIGHT_REM}rem`, overflow: "hidden", position: "relative" } : {}}
          >
            <MarkdownRenderer content={description} emptyLabel="Pas de compte-rendu" />
            {!expanded && canExpand && description.length > 0 && (
              <div
                style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  height: "2rem",
                  background: "linear-gradient(to bottom, transparent, var(--bs-body-bg))",
                }}
              />
            )}
          </div>
          {canExpand && (
            <Button variant="link" size="sm" className="ps-0" style={{ color: "var(--bs-blue)", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => e.target.style.color = "#b8c7f2"} onMouseLeave={(e) => e.target.style.color = "var(--bs-blue)"} onClick={() => setExpanded(!expanded)}>
              {expanded ? "Afficher moins" : "Afficher plus"}
            </Button>
          )}
          <hr />
        </div>
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Back to list */}
      {fromList && (
        <Button variant="link" className="mb-2 ps-0 btn-topbar-icon" onClick={() => navigate(fromList, { state: listState })}>
          <FaArrowLeft className="me-2" /> Retour à la liste
        </Button>
      )}

      {/* Ticket header card */}
      <Card className="border-left-warning shadow mt-3 mb-3">
        <CardBody>
          <h1 className="h3 mb-0 text-gray-800 d-flex align-items-center gap-2 flex-wrap">
            {!isClosed && (
              <span className={`badge bg-${priorityBadge.variant} flex-shrink-0`} style={{ fontSize: 12 }}>
                {priorityBadge.label}
              </span>
            )}
            <span className="d-flex align-items-center" style={{ wordBreak: "break-word", minWidth: 0 }}>
              <b className="me-2 flex-shrink-0">ID:{ticket.id}</b>
              {ticket.title}
            </span>
          </h1>
        </CardBody>
      </Card>

      {/* Action buttons */}
      <div className="d-flex flex-wrap gap-2 mb-2 justify-content-end">
        {hasPermission("CREATE_INTERVENTION") && isClientActive && !isClosed && !isApproved && (
          <button className="btn btn-primary btn-icon-split" onClick={() => setShowNewIntervention(true)}>
            <span className="icon text-white-50"><MdAssignmentAdd style={{ color: "#fff" }} /></span>
            <span className="text">Ajouter une intervention</span>
          </button>
        )}
        {hasPermission("OPEN_TICKET") && isClientActive && isClosed && (
          <button className="btn btn-success btn-icon-split" onClick={() => setShowConfirmReopen(true)}>
            <span className="icon text-white-50"><FaFolderPlus style={{ color: "#fff" }} /></span>
            <span className="text">Ré-ouvrir le ticket</span>
          </button>
        )}
        {hasPermission("CLOSE_TICKET") && isInProgress && isEditable && (
          <button className="btn btn-warning btn-icon-split" onClick={() => setShowConfirmClose(true)}>
            <span className="icon text-white-50"> <FaLock style={{ color: "#fff" }} /></span>
            <span className="text">Clôturer le ticket</span>
          </button>
        )}
        <button className="btn btn-secondary" onClick={handlePrint}>
          <FaPrint className="me-2" /> Imprimer
        </button>
      </div>

      <Row>
        {/* ── Left column ── */}
        <Col xl={8}>

          {/* Ticket info card */}
          <Card className="border-left-warning shadow mt-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="text-warning fw-semibold m-0">Information ticket</h6>
              {hasPermission("EDIT_TICKET") && isEditable && (
                <Button variant="link" size="sm" className="text-warning" onClick={() => setShowEditTicket(true)}>
                  <FaPenToSquare />
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              <Row className="mb-4">
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <IoTime className="text-muted mt-1 me-1" size={16} />
                    <div>
                      <div className="text-muted small">Date début ticket</div>
                      <div>{ticket.start_date ? formatDateTime(ticket.start_date) : "Non renseigné"}</div>
                    </div>
                  </div>
                </Col>
                
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <AiFillTags className="text-muted mt-1 me-1" size={16} />
                    <div>
                      <div className="text-muted small">Type intervention</div>
                      <div>{ticket.type?.label}</div>
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className="d-flex align-items-start">
                    <FaPeopleCarry className="text-muted mt-1 me-1" size={16} />
                    <div>
                      <div className="text-muted small">Assigné(s)</div>
                      <div>
                        {(ticket.groups?.length > 0 || ticket.users?.length > 0) ? (
                          <>
                            {ticket.groups?.map(g => (
                              <div key={`g-${g.id}`} className="badge bg-secondary me-1 mb-1">{g.name}</div>
                            ))}
                            {ticket.users?.map(u => (
                              <div key={`u-${u.id}`}>{u.name} {u.surname}</div>
                            ))}
                          </>
                        ) : (
                          <span className="text-muted">Non assigné</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>

                {ticket.close_date && (
                <Col md={4} className="mt-3 mt-md-0">
                  <div className="d-flex align-items-start">
                    <IoTime className="text-muted mt-1 me-1" size={16} />
                    <div>
                      <div className="text-muted small">Date clôture</div>
                      <div>{formatDateTime(ticket.close_date)}</div>
                    </div>
                  </div>
                </Col>
              )}
              {ticket.billing_method && (
                <Col md={4} className="mt-3 mt-md-0">
                  <div className="d-flex align-items-start">
                    <FaFileInvoiceDollar className="text-muted mt-1 me-1" size={16} />
                    <div>
                      <div className="text-muted small">Mode de facturation</div>
                      <div>{ticket.billing_method.label}</div>
                    </div>
                  </div>
                </Col>
              )}
              {approver && (
                <Col md={4} className="mt-3 mt-md-0">
                  <div className="d-flex align-items-start">
                    <FaUser className="text-muted mt-1 me-1" size={16} />
                    <div>
                      <div className="text-muted small">Approuvé par</div>
                      <div>{approver.surname} {approver.name}</div>
                    </div>
                  </div>
                </Col>
              )}
              </Row>

              <div className="text-muted small mb-2">Description</div>
              <ExpandableText text={ticket.description} />
              <hr />

              <div className="text-muted small mb-3">Intervention(s)</div>
              {sortedInterventions.length === 0 ? (
                <div className="text-muted">Aucune intervention</div>
              ) : (
                sortedInterventions.map(i => <InterventionItem key={i.id} intervention={i} />)
              )}
            </Card.Body>
          </Card>

          {/* Transport tracking card */}
          <Card className="border-left-secondary shadow mt-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="text-secondary fw-semibold m-0">Suivi transport</h6>
              {isEditable && hasPermission("CREATE_INTERVENTION") && (
                <Button
                  variant="link"
                  size="sm"
                  className="p-1 text-secondary"
                  onClick={() => setShowTransportModal(true)}
                >
                  <RiMailAddFill size={18} />
                </Button>
              )}
            </Card.Header>
            <Card.Body >
              {transportsLoading ? (
                <div >
                  <Spinner animation="border" size="sm" />
                </div>
              ) : transports.length === 0 ? (
                <div className="text-muted">Aucun transport</div>
              ) : (
                <Table hover responsive className="mb-0" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Transporteur</th>
                      <th>N° suivi</th>
                      <th>Prix</th>
                      {isEditable && hasPermission("EDIT_TICKET") && <th className="text-center">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {transports.map(t => (
                      <tr key={t.id}>
                         <td className="align-middle">
                          {t.carrier?.name || "—"}  
                        </td>
                        <td className="align-middle">
                          <span className="font-monospace">{t.tracking_number || "—"}</span>
                        </td>
                        <td className="align-middle">
                          {t.price != null ? `${t.price} €` : "—"}
                        </td>
                        {isEditable && hasPermission("EDIT_TICKET") && (
                          <td className="align-middle text-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1 text-danger"
                              onClick={() => openDeleteTransport(t.id)}
                            >
                              <FaTrash size={14} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* ── Right column ── */}
        <Col xl={4}>

          {/* Client info card */}
          <Card className="border-left-warning shadow mt-3">
            <Card.Header><h6 className="text-warning fw-semibold m-0">Informations client</h6></Card.Header>
            <Card.Body>
              <div className="mb-2"><FaUser className="text-muted me-2" /> {client?.name || "Non renseigné"}</div>
              <div className="mb-2"><FaMapMarkerAlt className="text-muted me-2" /> {client?.address || "Non renseigné"}</div>
              <div className="mb-2"><FaPhone className="text-muted me-2" /> {client?.phone_number || "Non renseigné"}</div>
              <div className="mb-2"><RiInfoCardLine className="text-muted me-2" /> {contract?.type?.name || "Pas de contrat"}</div>
              <Button variant="warning" className="w-100 mt-3" disabled={!client} onClick={() => navigate(`/client/${client.id}`)}>
                Voir le client
              </Button>
            </Card.Body>
          </Card>

          {/* Custom field values card */}
          {Array.isArray(champValeursData?.data) && champValeursData.data.length > 0 && (
            <Card className="border-left-warning shadow mt-3">
              <Card.Header><h6 className="text-warning fw-semibold m-0">Champs complémentaires</h6></Card.Header>
              <Card.Body>
                {champValeursData.data.map(({ value, custom_field }) => (
                  <div key={custom_field.id} className="mb-2">
                    <div className="text-muted small">{custom_field.label}</div>
                    <div className="fw-medium">{value || "—"}</div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Attached files card */}
          <Card className="border-left-warning shadow mt-3">
            <Card.Header><h6 className="text-warning fw-semibold m-0">Fichiers joints</h6></Card.Header>            
            <Card.Body>
              <TicketFiles
                ticketId={ticketId}
                canUpload={isEditable && hasPermission("CREATE_FILE_TICKET")}
                canDelete={isEditable && hasPermission("DELETE_FILE_TICKET")}
              />
            </Card.Body>
          </Card>

          {/* Danger zone */}
          {isEditable && hasPermission("DELETE_TICKET") && (
          <div className="mt-3">
            <Button
              variant="danger"
              className="w-100"
              onClick={() => setShowDeleteTicketModal(true)}
            >
              <FaTrash className="me-2" /> Supprimer le ticket
            </Button>
          </div>
        )}
        </Col>
      </Row>

      {/* ── Modals ── */}

      {/* Delete intervention confirmation */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmation</Modal.Title></Modal.Header>
        <Modal.Body>Êtes-vous sûr de vouloir supprimer cette intervention ?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? "Suppression..." : "Supprimer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reopen ticket confirmation */}
      <Modal show={showConfirmReopen} onHide={() => setShowConfirmReopen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Confirmation</Modal.Title></Modal.Header>
        <Modal.Body>Êtes-vous sûr de vouloir ré-ouvrir ce ticket ?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmReopen(false)}>Annuler</Button>
          <Button
            variant="success"
            onClick={handleReopenTicket}
            disabled={reopenMutation.isPending}
          >
            {reopenMutation.isPending ? "Réouverture..." : "Ré-ouvrir"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Close ticket with billing method selection */}
      <Modal show={showConfirmClose} onHide={() => setShowConfirmClose(false)} centered>
        <Modal.Header closeButton><Modal.Title>Clôturer le ticket</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Êtes-vous sûr de vouloir clôturer ce ticket ?</p>
          <Form.Group className="mt-3">
            <Form.Label>Méthode de facturation <span className="text-danger">*</span></Form.Label>
            {facturationLoading ? (
              <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
            ) : (
              <Form.Select
                value={selectedFacturationMode || ""}
                onChange={(e) => setSelectedFacturationMode(Number(e.target.value))}
                required
              >
                <option value="">Sélectionner une méthode</option>
                {availableFacturationModes.map(m => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          {/* Champs personnalisés applicables à cette clôture */}
          {champsACloturer.length > 0 && (
            <>
              <hr />
              <p className="fw-semibold mb-2">Champs complémentaires</p>
              {champsACloturer.map(champ => (
                <CustomFieldInput
                  key={champ.id}
                  champ={champ}
                  value={customFieldValues[champ.id] ?? ""}
                  onChange={val => setCustomFieldValues(prev => ({ ...prev, [champ.id]: val }))}
                />
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmClose(false)}>Annuler</Button>
          <Button variant="warning" onClick={handleCloseTicket} disabled={closeTicketMutation.isPending || !selectedFacturationMode}>
            {closeTicketMutation.isPending ? "Clôture..." : "Clôturer"}
          </Button>
        </Modal.Footer>
      </Modal>

       {/* Delete ticket confirmation */}
      <Modal show={showDeleteTicketModal} onHide={() => setShowDeleteTicketModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Supprimer le ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer définitivement le ticket{" "}
          <strong>#{ticket?.id}</strong> ? Cette action est irréversible.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteTicketModal(false)}>
            Annuler
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDeleteTicket}
            disabled={deleteTicketMutation.isPending}
          >
            {deleteTicketMutation.isPending ? "Suppression..." : "Supprimer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete transport confirmation */}
      <Modal show={showDeleteTransport} onHide={() => setShowDeleteTransport(false)} centered>
        <Modal.Header closeButton><Modal.Title>Supprimer le transport</Modal.Title></Modal.Header>
        <Modal.Body>Êtes-vous sûr de vouloir supprimer ce transport ?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteTransport(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleConfirmDeleteTransport} disabled={deleteTransport.isPending}>
            {deleteTransport.isPending ? "Suppression..." : "Supprimer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Transport create modal */}
      <TransportModal
        show={showTransportModal}
        onHide={() => setShowTransportModal(false)}
        onSubmit={handleTransportSubmit}
        isPending={createTransport.isPending}
        initialData={null}
      />

      {/* CRUD modals for interventions and ticket edit */}
      <NewIntervention showNewIntervention={showNewIntervention} setShowNewIntervention={setShowNewIntervention} ticketId={ticketId} />
      {showEditTicket && <EditTicket showEditTicket={showEditTicket} setShowEditTicket={setShowEditTicket} ticket={ticket} />}
      <EditIntervention show={showEditIntervention} onHide={() => setShowEditIntervention(false)} intervention={selectedIntervention} ticketId={ticketId} />
    </>
  );
};

export default Ticket;